import { RUN, SPAWN } from '../config/gameConfig';
import type { CollectibleKind, ObstacleKind } from '../types';
import { pickIndex } from '../utils/rng';

export interface SpawnEvent {
  type: 'obstacle' | 'collectible';
  lane: number;
  kind: ObstacleKind | CollectibleKind;
  speedFactor: number;
  weaves?: boolean;
  weaveToLane?: number;
}

export interface SpawnIntensityMods {
  rushActive: boolean;
  finalStretch: boolean;
  heatRatio: number;
}

/**
 * Fair procedural spawn planner.
 * Ensures at least one safe lane and a minimum reaction window between hazards.
 */
export class SpawnSystem {
  private rng: () => number;
  private timer = 0;
  private nextGap: number = SPAWN.maxGapMs;
  private recentLanes: number[] = [];
  private elapsed = 0;

  constructor(rng: () => number = Math.random) {
    this.rng = rng;
  }

  reset(rng?: () => number): void {
    if (rng) this.rng = rng;
    this.timer = 0;
    this.nextGap = SPAWN.maxGapMs;
    this.recentLanes = [];
    this.elapsed = 0;
  }

  update(
    deltaMs: number,
    durationSec: number,
    mods: SpawnIntensityMods = { rushActive: false, finalStretch: false, heatRatio: 0 },
  ): SpawnEvent[] {
    this.elapsed += deltaMs;
    this.timer += deltaMs;
    const events: SpawnEvent[] = [];
    if (this.timer < this.nextGap) return events;
    this.timer = 0;

    const progress = Math.min(1, this.elapsed / (durationSec * 1000));
    const densify =
      progress * 0.85 +
      mods.heatRatio * 0.12 +
      (mods.rushActive ? 0.2 : 0) +
      (mods.finalStretch ? 0.25 : 0);

    this.nextGap =
      SPAWN.maxGapMs - (SPAWN.maxGapMs - SPAWN.minGapAtMaxDiff) * Math.min(1, densify);
    if (mods.rushActive) this.nextGap *= 0.72;
    if (mods.finalStretch) this.nextGap *= 0.68;
    this.nextGap *= 0.85 + this.rng() * 0.28;

    const obstacle = this.planObstacle(progress, mods);
    if (obstacle) events.push(obstacle);

    const dualChance =
      SPAWN.dualSpawnChance +
      (mods.rushActive ? 0.18 : 0) +
      (mods.finalStretch ? 0.22 : 0) +
      mods.heatRatio * 0.1;
    if (
      obstacle &&
      progress >= SPAWN.dualSpawnAtProgress - (mods.rushActive ? 0.12 : 0) &&
      this.rng() < dualChance
    ) {
      const second = this.planObstacle(progress, mods, obstacle.lane);
      if (second && second.lane !== obstacle.lane) {
        const occupied = new Set([obstacle.lane, second.lane]);
        if (occupied.size < RUN.lanes) events.push(second);
      }
    }

    const collectChance =
      SPAWN.collectibleChance + (mods.finalStretch ? 0.08 : 0) - (mods.rushActive ? 0.05 : 0);
    if (this.rng() < collectChance) {
      const avoid = events.filter((e) => e.type === 'obstacle').map((e) => e.lane);
      const collectible = this.planCollectible(avoid[0]);
      if (collectible && !avoid.includes(collectible.lane)) events.push(collectible);
    }

    return events;
  }

  private planObstacle(
    progress: number,
    mods: SpawnIntensityMods,
    forceAvoidLane?: number,
  ): SpawnEvent | null {
    const blocked = new Set(this.recentLanes.slice(-2));
    if (forceAvoidLane != null) blocked.add(forceAvoidLane);
    const free = [...Array(RUN.lanes).keys()].filter((l) => !blocked.has(l));
    const pool =
      free.length > 0
        ? free
        : [...Array(RUN.lanes).keys()].filter((l) => l !== forceAvoidLane);
    if (pool.length === 0) return null;

    const openLane = pickIndex(this.rng, RUN.lanes);
    let lane = pool[pickIndex(this.rng, pool.length)] ?? 0;
    if (blocked.size >= RUN.lanes - 1) {
      lane = openLane === forceAvoidLane ? (pool[0] ?? openLane) : openLane;
    }

    this.recentLanes.push(lane);
    if (this.recentLanes.length > 6) this.recentLanes.shift();

    const kind = this.pickObstacleKind(progress, mods);
    const difficultyBoost =
      1 + progress * 0.4 + (mods.rushActive ? 0.15 : 0) + (mods.finalStretch ? 0.2 : 0);
    const base =
      kind === 'slowCar' ? 0.35 : kind === 'truck' ? 0.48 : kind === 'boda' ? 1.25 : 0.75;

    const weaveChance = mods.rushActive
      ? SPAWN.rushWeaveChance
      : progress >= SPAWN.weaveAtProgress
        ? SPAWN.weaveChance + mods.heatRatio * 0.15
        : 0;
    const canWeave =
      (kind === 'traffic' || kind === 'boda' || kind === 'slowCar') &&
      this.rng() < weaveChance;
    let weaveToLane: number | undefined;
    if (canWeave) {
      const options = [...Array(RUN.lanes).keys()].filter(
        (l) => l !== lane && l !== forceAvoidLane,
      );
      if (options.length > 0) {
        weaveToLane = options[pickIndex(this.rng, options.length)];
      }
    }

    return {
      type: 'obstacle',
      lane,
      kind,
      speedFactor: base * difficultyBoost,
      weaves: weaveToLane != null,
      weaveToLane,
    };
  }

  private planCollectible(avoidLane?: number): SpawnEvent | null {
    const lanes = [...Array(RUN.lanes).keys()].filter((l) => l !== avoidLane);
    if (lanes.length === 0) return null;
    const lane = lanes[pickIndex(this.rng, lanes.length)] ?? 0;
    const kind: CollectibleKind = this.rng() < SPAWN.bonusChance ? 'bonus' : 'fare';
    return { type: 'collectible', lane, kind, speedFactor: 1 };
  }

  private pickObstacleKind(progress: number, mods: SpawnIntensityMods): ObstacleKind {
    const roll = this.rng();
    const specialGate = SPAWN.specialObstacleAtProgress - (mods.finalStretch ? 0.12 : 0);
    if (progress > specialGate && roll > (mods.rushActive ? 0.55 : 0.68)) {
      return roll > 0.85 ? 'truck' : 'boda';
    }
    if (roll < 0.18) return 'pothole';
    if (roll < 0.32) return 'cones';
    if (roll < 0.55) return 'slowCar';
    return 'traffic';
  }
}
